import React from 'react';
import styled from '@emotion/styled';
import type { IconName, IconProps, ButtonProps } from '@hotjar/hotsauce';
import { tokens, Button, Icon, Loader, Text, Tooltip } from '@hotjar/hotsauce';

const Wrapper = styled.div`
  ${tokens.flexCenter()};
  padding: ${tokens.spacer.x3};
  flex-direction: column;
  text-align: center;
  margin: 0 auto;
  max-width: 650px;
`;

const Banner = styled.div`
  margin-bottom: ${tokens.spacer.x3};
  color: ${tokens.color.text_disabled};
`;

const Img = styled.img`
  max-width: 250px;
`;

const Title = styled(Text)`
  margin: 0 0 ${tokens.spacer.x1};
`;

const Subtitle = styled(Text)`
  margin: 0 0 ${tokens.spacer.x3};
`;

const CondensedTitle = styled(Text)`
  margin: 0 0 ${tokens.spacer.x0};
`;

const CondensedSubtitle = styled(Text)`
  margin: 0 0 ${tokens.spacer.x2};
`;

interface EmptyStateProps {
  img?: string;
  icon?: IconName;
  iconColor?: IconProps['color'];
  loading?: boolean;
  title: string;
  subtitle: React.ReactNode;
  buttonKind?: ButtonProps['kind'];
  buttonIcon?: IconName;
  buttonText?: string;
  buttonIsDisabled?: boolean;
  buttonIsDisabledTooltip?: string;
  onButtonClick?: () => void;
  kind?: 'default' | 'condensed';
  colorsInversed?: boolean;
}

export const EmptyState = ({
  img,
  icon = 'warning_alt',
  iconColor,
  loading = false,
  title,
  kind = 'default',
  buttonIcon,
  buttonText,
  buttonKind = 'primary',
  buttonIsDisabled,
  buttonIsDisabledTooltip,
  onButtonClick,
  subtitle,
  colorsInversed = false,
}: EmptyStateProps): JSX.Element => {
  const isCondensed = kind === 'condensed';
  const mainColor = colorsInversed ? 'text_default_inverse' : 'text_default';
  const subColor = colorsInversed ? 'text_default_inverse' : 'text_light';

  return (
    <Wrapper data-testid="empty-state">
      <Banner>
        {loading && <Loader size="xl" />}
        {!loading &&
          (img ? (
            <Img alt={title} src={img} />
          ) : (
            <Icon size="l" color={iconColor || mainColor} icon={icon} />
          ))}
      </Banner>
      {isCondensed ? (
        <>
          <CondensedTitle as="span" textType="subtitle1" textColor={mainColor}>
            {title}
          </CondensedTitle>
          <CondensedSubtitle as="span" textType="caption" textColor={subColor}>
            {subtitle}
          </CondensedSubtitle>
        </>
      ) : (
        <>
          <Title as="h5" textType="header5" textColor={mainColor}>
            {title}
          </Title>
          <Subtitle as="p" textType="body1" textColor={subColor}>
            {subtitle}
          </Subtitle>
        </>
      )}
      {buttonText && onButtonClick ? (
        <Tooltip text={buttonIsDisabledTooltip || ''} isDisabled={!buttonIsDisabled}>
          <span>
            <Button
              kind={buttonKind}
              onClick={onButtonClick}
              icon={buttonIcon}
              isDisabled={buttonIsDisabled}
            >
              {buttonText}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </Wrapper>
  );
};
